import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { Row, Separator } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';
import { RRRFetch } from '@cuemath/rest-api';
import { getPubnub } from '../pubnub-manager';
import {
  mapBackendMessage,
  FILE_UPLOAD,
  getMessageType,
  getDateLabel,
  parseEmailList,
} from '../utils';
import {
  TICKET_STATUS,
  RESOLVED_STATUSES,
  ERROR_AUTO_CLEAR_MS,
  TICKET_STATUS_CHANGE_EVENTS,
  SENIOR_ROLES,
  TICKET_SOURCE,
} from '../constants';
import { usePostMessage, useGetMessages, usePatchTicket } from 'actions/support-agent';
import {
  ThreadContainer,
  ThreadScroll,
  ThreadInner,
  DateSeparator,
  DateSeparatorLabel,
  SA_COLORS,
} from '../styled-comps';
import ThreadItem from './thread-item';
import ThreadHeader from './thread-header';
import ThreadComposer from './thread-composer';
import useEmailChain from './email-chain';
import Icon from './icon';

// ── Local styled components ───────────────────────────────────────────

const ThreadErrorMsg = styled(Text)`
  && {
    font-size: 12px;
    color: ${SA_COLORS.ERROR_600};
    padding: 8px 48px;
    background: ${SA_COLORS.ERROR_50};
    border-top: 1px solid ${SA_COLORS.ERROR_200};
    text-align: center;
  }
`;

const ReadOnlyBanner = styled(Row)`
  && {
    padding: 12px 48px;
    border-top: 1px solid ${SA_COLORS.BORDER_TERTIARY};
    background: ${SA_COLORS.BG_SECONDARY};
    justify-content: center;
    gap: 8px;
    align-items: center;
  }
`;

const ReadOnlyText = styled(Text)`
  && {
    font-size: 13px;
    color: ${SA_COLORS.TEXT_TERTIARY};
  }
`;

const UnassignedBanner = styled(Row)`
  && {
    padding: 10px 24px;
    background: ${SA_COLORS.WARNING_100};
    border-bottom: 1px solid ${SA_COLORS.WARNING_300};
    align-items: center;
    gap: 10px;
  }
`;

const UnassignedText = styled(Text)`
  && {
    flex: 1;
    font-size: 13px;
    font-weight: 500;
    color: ${SA_COLORS.WARNING_700};
  }
`;

const AssignBtn = styled.button`
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  background: ${SA_COLORS.INFO_900};
  color: white;
  border: none;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

const LoadOlderBtn = styled.button`
  display: block;
  margin: 8px auto 16px;
  padding: 6px 16px;
  font-size: 12px;
  font-weight: 600;
  color: ${SA_COLORS.INFO_600};
  background: ${SA_COLORS.BG_SECONDARY};
  border: 1px solid ${SA_COLORS.BORDER_SECONDARY};
  cursor: pointer;

  &:hover {
    background: ${SA_COLORS.BG_TERTIARY};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

// ── PubNub message mapper ─────────────────────────────────────────────

const mapPubnubMessage = msg =>
  mapBackendMessage({ ...msg, timestamp: msg.timestamp || msg.at });

// ── Thread component ──────────────────────────────────────────────────

const Thread = ({
  activeTicket,
  agentMe,
  userRole,
  onAssign,
  onMarkRead,
  onTicketUpdated,
  onPickTicket,
}) => {
  const [items, setItems] = useState([]);
  const [val, setVal] = useState('');
  const [internal, setInternal] = useState(false);
  const [threadError, setThreadError] = useState(null);
  const [showResolveConfirm, setShowResolveConfirm] = useState(false);
  const [resolved, setResolved] = useState(false);

  // Email composer state
  const isEmailTicket = activeTicket?.source === TICKET_SOURCE.EMAIL;
  const [emailSubject, setEmailSubject] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [emailBcc, setEmailBcc] = useState('');
  const [showBcc, setShowBcc] = useState(false);

  // Email chain hook
  const {
    quotedAreaRef,
    quotedTextIncluded,
    toggleQuotedText,
    growChainFromPubnub,
    growChainFromAgentReply,
    buildSendHtml,
  } = useEmailChain(activeTicket, items, internal);

  // Reset email fields when switching tickets
  useEffect(() => {
    if (isEmailTicket && activeTicket?.email_subject) {
      const subj = activeTicket.email_subject || '';
      setEmailSubject(subj.startsWith('Re:') ? subj : `Re: ${subj}`);
    } else {
      setEmailSubject('');
    }
    setEmailCc((activeTicket?.email_cc || []).join(', '));
    setEmailBcc('');
    setShowBcc(false);
  }, [activeTicket?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync composer tab: if can_reply is false but can_add_note is true, default to note mode
  useEffect(() => {
    const p = activeTicket?.permissions || {};
    if (!p.can_reply && p.can_add_note) {
      setInternal(true);
    } else if (p.can_reply) {
      setInternal(false);
    }
  }, [activeTicket?.id, activeTicket?.permissions]);

  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const scrollRef = useRef(null);
  const prependedRef = useRef(false);

  // --- API hooks ---
  useEffect(() => {
    if (!threadError) return;
    const timer = setTimeout(() => setThreadError(null), ERROR_AUTO_CLEAR_MS);
    return () => clearTimeout(timer);
  }, [threadError]);

  const lastTempIdRef = useRef(null);
  const { post: postMessage } = usePostMessage({
    onComplete: error => {
      if (error) {
        setThreadError(typeof error === 'string' ? error : 'Failed to send message');
        if (lastTempIdRef.current) {
          setItems(prev => prev.filter(i => i.id !== lastTempIdRef.current));
          lastTempIdRef.current = null;
        }
      } else {
        if (onTicketUpdated) onTicketUpdated();
      }
    },
  });

  // --- File upload ---
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [attachment, setAttachment] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!uploadError) return;
    const timer = setTimeout(() => setUploadError(null), ERROR_AUTO_CLEAR_MS);
    return () => clearTimeout(timer);
  }, [uploadError]);

  const handleFileSelect = useCallback(async e => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;

    if (file.size > FILE_UPLOAD.MAX_SIZE) {
      setUploadError('File must be under 10 MB');
      return;
    }
    if (!FILE_UPLOAD.ALLOWED_TYPES.includes(file.type)) {
      setUploadError('Unsupported file type');
      return;
    }
    setUploading(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await RRRFetch.fetch('/api/v1/chatbot/uploads/', {
        method: 'POST',
        body: formData,
      });

      const url = data?.url;
      if (!url) {
        setUploadError('Upload failed');
        setUploading(false);
        return;
      }
      setAttachment({
        url,
        name: file.name,
        messageType: getMessageType(file.type),
      });
    } catch (err) {
      setUploadError(err?.error?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }, []);

  const patchActionTypeRef = useRef(null);
  const { patch: patchTicket, isProcessing: resolveProcessing } = usePatchTicket({
    onComplete: error => {
      const action = patchActionTypeRef.current;
      patchActionTypeRef.current = null;
      if (error) {
        if (action === 'resolve') {
          setThreadError(typeof error === 'string' ? error : 'Failed to resolve ticket');
        }
      } else {
        if (action === 'resolve') {
          setResolved(true);
          if (onTicketUpdated) onTicketUpdated();
        } else if (action === 'mark_read' && onMarkRead) {
          onMarkRead(activeTicket?.id);
        }
      }
    },
  });

  const {
    data: olderData,
    getAll: getOlderMessages,
    isProcessing: loadingOlder,
    isProcessingFailed: olderMsgsFailed,
  } = useGetMessages();

  // Fetch initial messages when ticket changes
  const initialLoadRef = useRef(false);
  useEffect(() => {
    if (activeTicket?.id) {
      initialLoadRef.current = true;
      markedReadRef.current = null;
      setItems([]);
      setCursor(null);
      setHasMore(false);
      setResolved(false);
      setVal('');
      getOlderMessages(activeTicket.id, { caller_type: 'agent' });
    }
  }, [activeTicket?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (olderMsgsFailed)
      setThreadError(
        initialLoadRef.current
          ? 'Failed to load messages.'
          : 'Failed to load older messages.',
      );
  }, [olderMsgsFailed]);

  // Clear the optimistic `resolved` flag when the backend status moves
  // back to a non-resolved value (e.g. ticket reopened by the customer).
  useEffect(() => {
    const status = (activeTicket?.status || '').toUpperCase();
    if (!RESOLVED_STATUSES.includes(status)) {
      setResolved(false);
    }
  }, [activeTicket?.status]);

  // Handle messages response (initial load or older messages)
  useEffect(() => {
    if (loadingOlder) return;
    const olderItems = olderData?.items;
    if (!Array.isArray(olderItems) || olderItems.length === 0) return;
    const mapped = olderItems.map(mapBackendMessage);

    setCursor(olderData.next_cursor || null);
    setHasMore(olderData.has_more || false);

    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      setItems(mapped);
    } else {
      prependedRef.current = true;
      const prevScrollHeight = scrollRef.current?.scrollHeight || 0;
      setItems(prev => {
        const prevIds = new Set(prev.map(i => i.id));
        const newItems = mapped.filter(m => !prevIds.has(m.id));
        if (newItems.length === 0) return prev;
        const mappedIds = new Set(mapped.map(i => i.id));
        const onlyInPrev = prev.filter(i => !mappedIds.has(i.id));
        return [...mapped, ...onlyInPrev];
      });
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight - prevScrollHeight;
        }
        prependedRef.current = false;
      });
    }
  }, [olderData, loadingOlder]);

  // Auto-scroll to bottom only on append (new messages), not on prepend
  useEffect(() => {
    if (scrollRef.current && !prependedRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [items.length]);

  // --- Mark as read when ticket opens with unread messages ---
  const markedReadRef = useRef(null);
  const unreadCount = activeTicket?.unread_count || 0;
  useEffect(() => {
    if (
      activeTicket?.id &&
      unreadCount > 0 &&
      markedReadRef.current !== activeTicket.id
    ) {
      markedReadRef.current = activeTicket.id;
      patchActionTypeRef.current = 'mark_read';
      const lastMsgId = items.length > 0 ? items[items.length - 1]?.id : undefined;
      patchTicket(activeTicket.id, { action: 'mark_read', up_to_message_id: lastMsgId });
    }
  }, [activeTicket?.id, unreadCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- PubNub: listen for transcript + internal messages ---
  useEffect(() => {
    if (!activeTicket?.pubnub_channel) return;
    const transcriptChannel = activeTicket.pubnub_channel;
    const internalChannel = transcriptChannel.replace(
      'support.',
      'support_agent_internal.',
    );

    const pubnub = getPubnub();
    if (!pubnub) return;

    const handleMessage = msg => {
      const newMsg = mapPubnubMessage(msg);

      setItems(prev => {
        if (msg.temp_id && prev.find(i => i.id === msg.temp_id)) return prev;
        if (msg.message_id) {
          const idx = prev.findIndex(i => i.id === msg.message_id);
          if (idx !== -1) {
            // agent-internal copy carries sentBy; merge into the stripped one
            if (newMsg.sentBy && !prev[idx].sentBy) {
              const next = [...prev];
              next[idx] = {
                ...prev[idx],
                sentBy: newMsg.sentBy,
                sentByName: newMsg.sentByName,
              };
              return next;
            }
            return prev;
          }
        }
        return [...prev, newMsg];
      });

      // Grow the email quote chain when a customer message arrives
      growChainFromPubnub(newMsg);

      if (TICKET_STATUS_CHANGE_EVENTS.includes(msg.system_event_type)) {
        if (onTicketUpdated) onTicketUpdated();
      }
    };

    const listener = {
      message: event => {
        if (event.channel !== transcriptChannel && event.channel !== internalChannel) {
          return;
        }
        handleMessage(event.message);
      },
    };
    pubnub.addListener(listener);

    return () => {
      pubnub.removeListener(listener);
    };
  }, [activeTicket?.pubnub_channel]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = useCallback(() => {
    const trimmed = val.trim();
    if (!trimmed && !attachment) return;

    const now = new Date();
    const ts = now.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    const tempId = crypto.randomUUID();
    lastTempIdRef.current = tempId;

    const msgType = attachment?.messageType || 'TEXT';
    const mediaUrl = attachment?.url || undefined;
    const content = trimmed || (attachment ? attachment.name : '');

    if (internal) {
      if (activeTicket?.id) {
        postMessage(
          {
            type: 'NOTE',
            content,
            message_type: msgType,
            ...(mediaUrl ? { media_url: mediaUrl } : {}),
            temp_id: tempId,
          },
          activeTicket.id,
        );
      }
      setItems(prev => [
        ...prev,
        {
          id: tempId,
          kind: 'note',
          body: trimmed,
          author: 'You (internal)',
          ts,
          messageType: msgType,
          mediaUrl: mediaUrl || null,
        },
      ]);
    } else {
      if (activeTicket?.id) {
        const payload = {
          content,
          message_type: msgType,
          ...(mediaUrl ? { media_url: mediaUrl } : {}),
          temp_id: tempId,
        };
        // Include email metadata for email tickets
        if (isEmailTicket) {
          if (emailSubject) payload.email_subject = emailSubject;
          const ccList = parseEmailList(emailCc);
          const bccList = parseEmailList(emailBcc);
          if (ccList.length) payload.email_cc = ccList;
          if (bccList.length) payload.email_bcc = bccList;
          if (msgType === 'TEXT') {
            payload.html_content = buildSendHtml(content);
          }
        }
        postMessage(payload, activeTicket.id);
      }

      // Grow the accumulated chain with this agent reply (skip attachments)
      if (isEmailTicket && msgType === 'TEXT') {
        growChainFromAgentReply(content, now);
      }

      setItems(prev => [
        ...prev,
        {
          id: tempId,
          kind: 'msg',
          who: 'agent',
          name: agentMe?.name || 'You',
          body: trimmed,
          ts,
          messageType: msgType,
          mediaUrl: mediaUrl || null,
          emailCc: isEmailTicket ? parseEmailList(emailCc, false) : null,
          emailBcc: isEmailTicket ? parseEmailList(emailBcc, false) : null,
        },
      ]);

      // Clear CC/BCC after sending (after setItems so optimistic item captures values)
      if (isEmailTicket) {
        setEmailCc('');
        setEmailBcc('');
        setShowBcc(false);
      }
    }

    setVal('');
    setAttachment(null);
  }, [
    val,
    internal,
    attachment,
    activeTicket?.id,
    postMessage,
    agentMe,
    isEmailTicket,
    emailSubject,
    emailCc,
    emailBcc,
    growChainFromAgentReply,
    buildSendHtml,
  ]);

  const handleKeyDown = useCallback(
    e => {
      if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;

      if (e.shiftKey) return;

      if (e.altKey) {
        e.preventDefault();
        const ta = e.target;
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        setVal(prev => prev.substring(0, start) + '\n' + prev.substring(end));
        requestAnimationFrame(() => {
          ta.selectionStart = ta.selectionEnd = start + 1;
        });
        return;
      }

      e.preventDefault();
      send();
    },
    [send],
  );

  const handleResolve = useCallback(() => {
    if (activeTicket?.id) {
      patchActionTypeRef.current = 'resolve';
      patchTicket(activeTicket.id, {
        action: 'update_status',
        status: TICKET_STATUS.RESOLVED,
      });
    }

    const now = new Date();
    const ts = now.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    setItems(prev => [
      ...prev,
      {
        id: `local-resolve-${Date.now()}`,
        kind: 'event',
        icon: 'check',
        title: 'Ticket marked as resolved',
        sub: `${ts} -- by You`,
      },
    ]);
    setShowResolveConfirm(false);
  }, [activeTicket?.id, patchTicket]);

  const handleLoadOlder = useCallback(() => {
    if (activeTicket?.id && cursor && hasMore && !loadingOlder) {
      getOlderMessages(activeTicket.id, {
        before: cursor,
        limit: 30,
        caller_type: 'agent',
      });
    }
  }, [activeTicket?.id, cursor, hasMore, loadingOlder, getOlderMessages]);

  const ticketId = activeTicket?.id || '--';
  const customerName = activeTicket?.customer_name || 'Customer';
  const initials = (customerName || '--').slice(0, 2).toUpperCase();
  const perms = activeTicket?.permissions || {};
  const isSenior = SENIOR_ROLES.includes(userRole);
  const headerParts = (isSenior
    ? [activeTicket?.agent_name, activeTicket?.team_name]
    : [activeTicket?.team_name]
  ).filter(Boolean);
  const headerLabel = headerParts.join(' | ');

  const dateLabels = useMemo(() => {
    const labels = [];
    let prevLabel = null;
    for (let i = 0; i < items.length; i++) {
      const label = getDateLabel(items[i].rawTs);
      labels.push(label && label !== prevLabel ? label : null);
      if (label) prevLabel = label;
    }
    return labels;
  }, [items]);

  return (
    <ThreadContainer>
      <ThreadHeader
        ticketId={ticketId}
        customerName={customerName}
        initials={initials}
        headerLabel={headerLabel}
        perms={perms}
        resolved={resolved}
        activeTicket={activeTicket}
        showResolveConfirm={showResolveConfirm}
        setShowResolveConfirm={setShowResolveConfirm}
        handleResolve={handleResolve}
        resolveProcessing={resolveProcessing}
      />
      {perms.can_assign &&
        !activeTicket?.assigned_agent_id &&
        !RESOLVED_STATUSES.includes((activeTicket?.status || '').toUpperCase()) && (
          <UnassignedBanner>
            <Icon name="user" size={16} />
            <UnassignedText>This ticket is not yet assigned to an agent</UnassignedText>
            <AssignBtn onClick={onAssign}>Assign to agent</AssignBtn>
          </UnassignedBanner>
        )}

      <ThreadScroll ref={scrollRef}>
        <ThreadInner>
          {hasMore && (
            <LoadOlderBtn onClick={handleLoadOlder} disabled={loadingOlder}>
              {loadingOlder ? 'Loading...' : 'Load older messages'}
            </LoadOlderBtn>
          )}
          {items.map((item, idx) => (
            <React.Fragment key={item.id || item.message_id || `${item.kind}-${idx}`}>
              {dateLabels[idx] && (
                <DateSeparator>
                  <DateSeparatorLabel>{dateLabels[idx]}</DateSeparatorLabel>
                </DateSeparator>
              )}
              <ThreadItem
                item={item}
                emailFrom={isEmailTicket ? activeTicket?.email_to : null}
                emailTo={isEmailTicket ? activeTicket?.email_from : null}
              />
            </React.Fragment>
          ))}
          <Separator height={40} transparent />
        </ThreadInner>
      </ThreadScroll>
      {threadError && <ThreadErrorMsg>{threadError}</ThreadErrorMsg>}
      {(perms.can_reply || perms.can_add_note) && (
        <ThreadComposer
          val={val}
          setVal={setVal}
          internal={internal}
          setInternal={setInternal}
          send={send}
          handleKeyDown={handleKeyDown}
          isEmailTicket={isEmailTicket}
          emailSubject={emailSubject}
          setEmailSubject={setEmailSubject}
          emailCc={emailCc}
          setEmailCc={setEmailCc}
          emailBcc={emailBcc}
          setEmailBcc={setEmailBcc}
          showBcc={showBcc}
          setShowBcc={setShowBcc}
          quotedTextIncluded={quotedTextIncluded}
          toggleQuotedText={toggleQuotedText}
          quotedAreaRef={quotedAreaRef}
          uploading={uploading}
          uploadError={uploadError}
          attachment={attachment}
          setAttachment={setAttachment}
          fileInputRef={fileInputRef}
          handleFileSelect={handleFileSelect}
          customerName={customerName}
          perms={perms}
        />
      )}
      {!perms.can_reply && !perms.can_add_note && (
        <ReadOnlyBanner>
          <Icon name="lock" size={14} />
          <ReadOnlyText>You can only view this conversation</ReadOnlyText>
        </ReadOnlyBanner>
      )}
    </ThreadContainer>
  );
};

export default Thread;
